import {
  Box,
  Palette,
  styled,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
} from '@mui/material'
import { TableProps } from '@mui/material/Table'

import { getColor } from '../../../utils/getColor.ts'

type StyledPaletteColors = {
  [K in keyof Palette]: Palette[K] extends { main: string } ? K : never
}[keyof Palette]

interface StyledTableContainerProps {
  /** Paleta de colores para el borde exterior, default inherit */
  bgColor?: StyledPaletteColors | string
}

/**
 * Contenedor estilizado para la tabla. Reemplaza a Paper/Box por TableContainer nativo de MUI.
 * Mantiene un borde neutro estándar para no saturar visualmente aplicaciones densas.
 *
 * @example
 * ```tsx
 * <StyledTableContainer bgColor="primary">
 *   <StyledTable size="small">
 *     Contenido de la tabla
 *   </StyledTable>
 * </StyledTableContainer>
 * ```
 *
 * @param props.bgColor Define la variante de color del tema a utilizar para bordes y fondo.
 * @author isi-template
 */
export const StyledTableContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<StyledTableContainerProps>(({ theme, bgColor }) => {
  const colors = getColor(theme, bgColor)

  return {
    width: '100%',
    overflowX: 'auto',
    // Aplicamos el borde tintado al contenedor
    border: `1px solid ${colors.borderColor}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  }
})
/**************************************************************************************************/

interface StyledTableRowProps {
  /** Color base para calcular el hover tintado específico de esta fila */
  bgColor?: StyledPaletteColors | string
  /** Activar o desactivar hover explícitamente para esta fila */
  hover?: boolean
  /** Fila intercalada explícita para esta fila */
  striped?: boolean
}

/**
 * Define los estilos para la fila de manera independiente.
 * Sobrescribe el color base del Body si se le especifica un bgColor (Jerarquía Nivel 2).
 *
 * @example
 * ```tsx
 * <StyledTableRow bgColor="secondary">
 *   <StyledTableCell>Fila destacada</StyledTableCell>
 * </StyledTableRow>
 * ```
 *
 * @param props.bgColor Color del tema para forzar en toda la fila.
 * @param props.hover Fuerza u oculta el estado hover individualmente.
 * @param props.striped Fuerza u oculta el estado intercalado individualmente.
 * @author isi-template
 */
export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'hover' && prop !== 'striped',
})<StyledTableRowProps>(({ theme, bgColor, hover, striped }) => {
  const colors = bgColor ? getColor(theme, bgColor) : null

  return {
    // 1. INYECCIÓN DE VARIABLES Y COLOR BASE DE LA FILA
    ...(colors && {
      '--row-bg': colors.bgColor,
      '--row-hover': colors.hoverColor,
      '--row-striped': colors.stripedColor,

      backgroundColor: 'var(--row-bg) !important',

      '& .MuiTableCell-root': {
        borderBottomColor: colors.borderColor,
        color: colors.textColor,
      },
    }),

    // 2. OVERRIDE MANUAL DE HOVER (Solo si se envía hover={true/false} explícito a la fila)
    ...(hover === false && {
      '&&&:hover': {
        // &&& para máxima especificidad CSS
        backgroundColor: colors ? 'var(--row-bg) !important' : 'transparent !important',
      },
    }),
    ...(hover === true &&
      colors && {
        '&&&:hover': {
          backgroundColor: 'var(--row-hover) !important',
        },
      }),

    // 3. OVERRIDE MANUAL DE STRIPED (Solo si se envía striped={true/false} explícito a la fila)
    ...(striped === false && {
      '&&&:nth-of-type(odd)': {
        backgroundColor: colors ? 'var(--row-bg) !important' : 'transparent !important',
      },
    }),
    ...(striped === true &&
      colors && {
        '&&&:nth-of-type(odd)': {
          backgroundColor: 'var(--row-striped) !important',
        },
      }),
  }
})

/**************************************************************************************************/

interface StyledTableHeadProps {
  /** Paleta de colores, default inherit */
  bgColor?: StyledPaletteColors | string
}

/**
 * Componente estilizado para el encabezado de la tabla. Reemplaza a TableHead.
 *
 * @example
 * ```tsx
 * <StyledTableHead bgColor="secondary">
 *   <StyledTableRow>
 *     <StyledTableCell>CÓDIGO</StyledTableCell>
 *     <StyledTableCell>DESCRIPCIÓN</StyledTableCell>
 *   </StyledTableRow>
 * </StyledTableHead>
 * ```
 *
 * @param props.bgColor Variante de color del tema a utilizar para el encabezado.
 * @author isi-template
 */
export const StyledTableHead = styled(TableHead, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<StyledTableHeadProps>(({ theme, bgColor }) => {
  const colors = getColor(theme, bgColor)

  return {
    '& .MuiTableCell-head': {
      backgroundColor: colors.bgColor,
      color: colors.textColor,
      fontWeight: 600,
      letterSpacing: '0.05em',
      borderBottom: `1px solid ${colors.borderColor}`,
      backgroundClip: 'padding-box',
      transition: theme.transitions.create(['background-color', 'color'], {
        duration: theme.transitions.duration.short,
      }),

      // ESTILOS POR DEFECTO (size="medium")
      ...theme.typography.body1,
      padding: theme.spacing(1.5, 2),
    },

    // ESTILOS PARA size="small"
    '& .MuiTableCell-sizeSmall': {
      ...theme.typography.body2,
      fontWeight: 600, // Sobrescribimos el peso de body2
      padding: theme.spacing(1, 1.5),
    },

    '& tr th:last-child': {
      borderRight: 0,
    },
  }
})

/*************************************************************************************************/

interface StyledTableBodyProps {
  /** Color base para calcular el hover/striped de todo el cuerpo */
  bgColor?: StyledPaletteColors | string
  /** Activar filas intercaladas globalmente */
  striped?: boolean
  /** Activar hover globalmente, default true */
  hover?: boolean
}

/**
 * Componente estilizado para el cuerpo de la tabla. Controla estados globales (striped/hover).
 *
 * @example
 * ```tsx
 * <StyledTableBody bgColor="primary" hover="{true}" striped="{true}">
 *   Filas de la tabla
 * </StyledTableBody>
 * ```
 *
 * @param props.bgColor Color base del tema para calcular contrastes.
 * @param props.striped Activa el sombreado intercalado en filas impares (default true).
 * @param props.hover Activa la animación al pasar el cursor (default false).
 * @author isi-template
 */
export const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'striped' && prop !== 'hover' && prop !== 'bgColor',
})<StyledTableBodyProps>(({ theme, striped = true, hover = false, bgColor }) => {
  const colors = getColor(theme, bgColor)

  return {
    // 1. Limpieza de bordes finales
    '&& .MuiTableRow-root .MuiTableCell-root:last-child': {
      borderRight: 'none',
    },
    '&& .MuiTableRow-root:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },

    // 2. Estado Global: Striped
    ...(striped && {
      '&& .MuiTableRow-root:nth-of-type(odd)': {
        // Lee la variable de la fila, si no existe, usa el color base del body
        backgroundColor: `var(--row-striped, ${colors.stripedColor}) !important`,
      },
    }),

    // 3. Estado Global: Hover
    ...(hover && {
      '&& .MuiTableRow-root:hover': {
        // Lee la variable de hover de la fila, si no existe, usa el del body
        backgroundColor: `var(--row-hover, ${colors.hoverColor}) !important`,
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shortest,
        }),
      },
    }),

    // 4. Estilos de Celda Base
    '& .MuiTableCell-body': {
      ...theme.typography.body2,
      padding: theme.spacing(1, 1.5),
      borderBottomColor: colors.borderColor,
    },
  }
})

interface StyledTableCellProps extends TableCellProps {
  /** Color de fondo custom para la celda */
  bgColor?: StyledPaletteColors | string
  /** Color de texto custom para la celda */
  textColor?: string
}
/**
 * Celda estilizada individualmente. Posee la prioridad más alta de diseño (Jerarquía Nivel 3: Cell > Row > Body).
 *
 * @example
 * ```tsx
 * <StyledTableCell bgColor="cyan" textColor="primary">
 *   Dato Prioritario
 * </StyledTableCell>
 * ```
 *
 * @param props.bgColor Color del tema a forzar en esta celda única.
 * @param props.textColor Color de texto personalizado para esta celda.
 * @author isi-template
 */
export const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'textColor',
})<StyledTableCellProps>(({ theme, bgColor, textColor }) => {
  const cellBg = bgColor ? getColor(theme, bgColor) : null
  const cellText = textColor ? getColor(theme, textColor) : null

  return {
    // JERARQUÍA NIVEL 3: La celda es soberana y le gana a la fila y al body
    ...(cellBg && {
      backgroundColor: `${cellBg.bgColor} !important`,
      color: `${cellBg.textColor} !important`,
      borderBottomColor: `${cellBg.borderColor} !important`,
    }),

    // Si solo mandan color de texto custom (sobrescribe todo texto anterior)
    ...(cellText && {
      color: `${cellText.strongTextColor || cellText.textColor} !important`,
    }),
  }
})

interface CustomTableProps extends TableProps {
  /**
   * 'auto': Ajusta las columnas al contenido.
   * 'fixed': Respeta anchos exactos (%) y trunca si es necesario.
   * @default 'auto'
   */
  layout?: 'auto' | 'fixed'
  /**
   * true: Ocupa el 100% de su contenedor, En mrt con desplegable, debe enviar la propiedad layout fixed.
   * false: Se ajusta al tamaño de su contenido.
   * @default true
   */
  fullWidth?: boolean
}

/**
 * Componente estilizado para la tabla principal.
 * Asegura el layout fijo y el espaciado correcto de los bordes.
 *
 * @example
 * ```tsx
 * <StyledTableContainer bgColor="primary">
 *   <StyledTable size="small">
 *     <StyledTableCaption bgColor="primary">Lista de Productos</StyledTableCaption>
 *     <StyledTableHead bgColor="purple">
 *       <StyledTableRow>
 *         <StyledTableCell>Código</StyledTableCell>
 *         <StyledTableCell>Nombre</StyledTableCell>
 *       </StyledTableRow>
 *     </StyledTableHead>
 *     <StyledTableBody bgColor="primary" hover="{false}" striped="{true}">
 *       <StyledTableRow>
 *         <StyledTableCell bgColor="cyan">Producto 1 1</StyledTableCell>
 *         <StyledTableCell>Producto 1 2</StyledTableCell>
 *       </StyledTableRow>
 *       <StyledTableRow bgColor="blue">
 *         <StyledTableCell>Producto 2 1</StyledTableCell>
 *         <StyledTableCell>Producto 2 2</StyledTableCell>
 *       </StyledTableRow>
 *     </StyledTableBody>
 *   </StyledTable>
 * </StyledTableContainer>
 * ```
 *
 * @param props.size Controla la densidad de la tabla (por defecto es 'small').
 * @param props.layout Controla el layout de la tabla (por defecto es 'auto').
 * @param props.fullWidth Controla si la tabla ocupa el 100% del contenedor (por defecto es true).
 * @author isi-template
 */
export const StyledTable = styled(
  ({ layout: _, fullWidth: __, size = 'small', ...props }: CustomTableProps) => (
    <Table size={size} {...props} />
  ),
)(({ layout = 'auto', fullWidth = true }) => ({
  width: fullWidth ? '100%' : 'auto',
  minWidth: fullWidth ? '100%' : 'auto',
  tableLayout: `${layout}` as 'auto' | 'fixed',
  borderCollapse: 'separate',
  borderSpacing: 0,
}))

interface StyledTableCaptionProps {
  /** Paleta de colores para heredar el color de texto y borde, default inherit */
  bgColor?: StyledPaletteColors | string
  /** Ubicación del caption respecto a la tabla, default 'top' */
  captionSide?: 'top' | 'bottom'
}

/**
 * Componente estilizado para el título o notas de la tabla (Caption).
 * Soporta posicionamiento superior o inferior con bordes dinámicos.
 *
 * @example
 * ```tsx
 * <StyledTableCaption bgColor="primary" captionSide="top">
 *   DESGLOSE DE COSTOS Y PRECIOS
 * </StyledTableCaption>
 * ```
 *
 * @param props.bgColor Variante de color del tema a heredar.
 * @param props.captionSide Posición del título ('top' o 'bottom'). Por defecto es 'top'.
 * @author isi-template
 */
export const StyledTableCaption = styled('caption', {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'captionSide',
})<StyledTableCaptionProps>(({ theme, bgColor, captionSide = 'top' }) => {
  const colors = getColor(theme, bgColor)
  const borderStyle = `1px solid ${colors.borderColor || theme.palette.divider}`

  return {
    '&&': {
      ...theme.typography.subtitle2,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: colors.textColor,
      textAlign: 'left',

      padding: theme.spacing(1.2, 1.5),

      captionSide: `${captionSide} !important` as any,

      borderBottom: captionSide === 'top' ? borderStyle : undefined,
      borderTop: captionSide === 'bottom' ? borderStyle : undefined,

      [theme.breakpoints.down('sm')]: {
        ...theme.typography.caption,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',

        // Espaciado ligeramente menor para móviles
        padding: theme.spacing(1),
      },
    },
  }
})
